import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useCustomHotkeys } from '../../../../common/hooks/useCustomHotkeys';
import { IOverlayLoaderStore } from '../../../../modules/common/OverlayLoader.store';
import { EditorMode } from '../../../../modules/editor/models/EditorModes';
import { AnnotationConfirmationModal, AnnotationConfirmationModalMode } from '../../../components/AnnotationConfirmationModal';
import { doNothing, doNothingAsync } from '../../../helpers/function.helpers';
import { useLoader } from '../../../helpers/useLoader';
import { IAnnotationsStore } from '../../annotation/annotations.store';
import { AnnotationNavigation } from '../../annotation/components/AnnotationNavigation';
import { IFreeDrawSegmentationService } from '../../annotation/freeDrawSegmentation.service';
import { IUndoRedoHistory } from '../../annotation/undoRedoHistory.service';
import { ProjectStatus } from '../../projects/projects.model';
import { useFreeAccessPermissions } from '../freeAccess.permissions';
import { IFreeAccessService } from '../freeAccess.service';
import { FreeAccessMode, IFreeAccessStore } from '../freeAccess.store';

export const FreeAccessAnnotateControlsContainer = observer((): ReactElement => {
  const freeAccessService = useInjection(IFreeAccessService.$);
  const freeAccessStore = useInjection(IFreeAccessStore.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const historyService = useInjection(IUndoRedoHistory.$);
  const freeAccessPermissions = useFreeAccessPermissions();
  const annotationsStore = useInjection(IAnnotationsStore.$);
  const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const [showAnnotationConfirmationModal, setShowAnnotationConfirmationModal] = useState(false);
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    if (freeAccessStore.freeAccessMode === FreeAccessMode.CORRECT) {
      await withLoaderAsync(async () => freeAccessService.correctAndSubmitAnnotationForReviewAsync(), 'submit-annotation-button');
    } else {
      await withLoaderAsync(async () => freeAccessService.submitAnnotationsForReviewAsync(), 'submit-annotation-button');
    }
    setShowAnnotationConfirmationModal(false);
  };

  const handleSave = async (): Promise<void> => {
    if (freeAccessStore.freeAccessMode === FreeAccessMode.CORRECT) {
      await withLoaderAsync(async () => freeAccessService.correctAnnotationAsync(), 'save-annotation-button');
    } else {
      await withLoaderAsync(async () => freeAccessService.editAnnotationAsync(), 'save-annotation-button');
    }
    setShowAnnotationConfirmationModal(false);
  };

  const handleSaveAsDraft = async (): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => freeAccessService.saveAnnotationDraftAsync(), 'save-draft-button');
  };

  const toggleAnnotationConfirmationModal = (): void => {
    setShowAnnotationConfirmationModal((prevState) => !prevState);
  };

  const handleDiscardConfirmed = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => freeAccessService.discardAnnotationAsync(getEditorMode()), 'discard-annotation-button');
  };

  const handleDiscardCanceled = (): void => {
    setShowDiscardConfirmationModal(false);
  };

  const handleDiscard = (): void => {
    setShowDiscardConfirmationModal(true);
  };

  const handleShowCancelAnnotation = async (): Promise<void> => {
    if (historyService.canUndo) {
      setShowCancelModal(true);
    } else {
      await handleCancelAnnotation();
    }
  };

  const handleHideCancelAnnotation = (): void => {
    setShowCancelModal(false);
  };

  const handleCancelAnnotation = async (): Promise<void> => {
    setShowCancelModal(false);
    historyService.clearHistory();
    await withLoaderAsync(async () => freeAccessService.cancelCreateAnnotationAsync(), 'cancel-annotation-button');
  };

  const getEditorMode = (): EditorMode => {
    if (freeAccessStore.freeAccessMode === FreeAccessMode.ANNOTATIONRESET) {
      return EditorMode.FREEACCESS_EDIT;
    }

    return EditorMode.FREEACCESS_ANNOTATION;
  };

  const disabled =
    annotationsStore.image === undefined ||
    freeDrawSegmentationService.freeDrawInProgress ||
    freeDrawSegmentationService.freeDrawFeature !== undefined ||
    overlayLoaderStore.isOverlayVisible();

  const stage = freeAccessStore.annotateStage;
  const mode = freeAccessStore.freeAccessMode;

  const hideDraft = !freeAccessPermissions.canSaveDraft(stage, mode);
  const canSave = freeAccessPermissions.canSave(mode);
  const canSubmit = freeAccessPermissions.canSubmit(mode);
  const canDiscard = freeAccessPermissions.canDiscard(stage);

  const editorMode = getEditorMode();

  useCustomHotkeys('ctrl+enter', handleSave, { enabled: !disabled && canSave.allowed }, [handleSave]);
  useCustomHotkeys('ctrl+shift+enter', toggleAnnotationConfirmationModal, { enabled: annotationsStore.projectStatus === ProjectStatus.Published }, [
    toggleAnnotationConfirmationModal,
  ]);

  return (
    <>
      <AnnotationNavigation
        canSave={canSave.allowed}
        canSubmit={canSubmit.allowed}
        canDiscard={canDiscard.allowed}
        hideDraft={hideDraft}
        disabled={disabled}
        editorMode={editorMode}
        onSubmit={handleSubmit}
        onSave={handleSave}
        onSaveAsDraft={handleSaveAsDraft}
        onSkip={doNothingAsync}
        hideSkip={true}
        hideAbandonDraft={true}
        onAbandonDraft={doNothing}
        showDiscardModal={showDiscardConfirmationModal}
        onDiscardCanceled={handleDiscardCanceled}
        onDiscardConfirmed={handleDiscardConfirmed}
        onDiscard={handleDiscard}
        onCancel={handleShowCancelAnnotation}
        canCancel={true}
        showCancelModal={showCancelModal}
        onCancelCancel={handleHideCancelAnnotation}
        onConfirmCancel={handleCancelAnnotation}
      />
      <AnnotationConfirmationModal
        showAnnotationConfirmationModal={showAnnotationConfirmationModal}
        annotationConfirmationModalMode={AnnotationConfirmationModalMode.SUBMIT}
        onAnnotationConfirm={handleSubmit}
        onAnnotationCancel={toggleAnnotationConfirmationModal}
        rejectAutoCompleteOptions={[]}
      />
    </>
  );
});
